import { formatEur } from "@gladis/shared";
import { formatDate as sharedFormatDate, formatMonth as sharedFormatMonth } from "@gladis/ui/i18n";

/**
 * Display rules, decision #30: share counts trimmed, prices and money at two decimal places.
 *
 * `10.0000 shares` is noise for a household that owns ten; `10.2534` is information. So trailing
 * zeros go and significant places stay.
 *
 * MONEY GOES THROUGH `formatEur`, the kernel's one formatter, so this module cannot render a euro
 * differently from savings or expenses. The conversion from decimal string to number happens HERE
 * and nowhere earlier: the wire keeps exactness, and the float exists only to be printed.
 */

/** `10`, `10.5`, `10.2534` — never `10.0000`. */
export function formatShares(value: string): string {
  return value.includes(".") ? value.replace(/\.?0+$/, "") : value;
}

/** A money figure, or an em-dash when there is no figure to show. */
export function formatMoney(value: string | null): string {
  return value === null ? "—" : formatEur(Number(value));
}

/** A price per share. Stored to four places, shown to two. */
export function formatPrice(value: string | null): string {
  return value === null ? "—" : formatEur(Number(value));
}

/**
 * A gain, with an explicit sign.
 *
 * THE SIGN AND A GLYPH CARRY THE MEANING, NOT COLOUR ALONE — 009's rule in `@gladis/ui`, because a
 * red number is invisible to a reader who cannot distinguish it from a green one.
 */
export function formatGain(value: string | null): string {
  if (value === null) return "—";
  const amount = Number(value);
  const sign = amount > 0 ? "+" : "";
  return `${sign}${formatEur(amount)}`;
}

export function formatPercent(value: string | null): string {
  if (value === null) return "—";
  const amount = Number(value);
  const sign = amount > 0 ? "+" : "";
  return `${sign}${amount.toFixed(2)}%`;
}

/** `2026-03` → `Mar 2026`, for the chart axis. */
export function formatMonth(key: string): string {
  const [year, month] = key.split("-").map(Number) as [number, number];
  return sharedFormatMonth(year, month, "short");
}

/** `2026-08-11` → `11 Aug 2026`. UTC throughout, so a date never shifts by a day. */
export function formatDate(value: string): string {
  return sharedFormatDate(value, "short");
}
